import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Copy,
  Cpu,
  Loader2,
  PlugZap,
  RefreshCw,
  Unplug,
  XCircle,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  createRealPlcAgentPairingCode,
  type PairingCodeResponse,
} from '../services/opennessAgent';
import { loadCodesysCapabilities, type CodesysCapabilitiesResponse } from '../services/codesysAgent';
import { useTiaAgentStatusStore } from '../stores/tiaAgentStatusStore';
import {
  CodesysCapabilityCard,
  pickCodesysAgent,
  readCodesysCapabilityView,
} from './CodesysCapabilityCard';

interface CodesysAgentSettingsDialogProps {
  isOpen: boolean;
  projectId?: string | null;
  onClose: () => void;
}

function formatTime(value?: number | null): string {
  return value ? new Date(value).toLocaleString() : '-';
}

export const CodesysAgentSettingsDialog: React.FC<CodesysAgentSettingsDialogProps> = ({
  isOpen,
  projectId,
  onClose,
}) => {
  const { language } = useTranslation();
  const zh = language === 'Chinese';
  const status = useTiaAgentStatusStore(state => state.status);
  const statusLoadState = useTiaAgentStatusStore(state => state.loadState);
  const statusError = useTiaAgentStatusStore(state => state.error);
  const refreshAgentStatus = useTiaAgentStatusStore(state => state.refresh);
  const startAgentStatus = useTiaAgentStatusStore(state => state.start);
  const [capabilities, setCapabilities] = useState<CodesysCapabilitiesResponse | null>(null);
  const [pairing, setPairing] = useState<PairingCodeResponse | null>(null);
  const [message, setMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [creatingCode, setCreatingCode] = useState(false);

  const codesysAgent = useMemo(() => {
    const online = status?.agents?.filter(agent => agent.status === 'online') ?? [];
    if (capabilities?.codesys) {
      return online.find(agent => agent.id === capabilities.agentId) ?? null;
    }
    return pickCodesysAgent(online, projectId);
  }, [status, capabilities, projectId]);
  const connectionState = status?.connectionState ?? status?.status;
  const capabilityRoot = capabilities?.codesys
    ? { codesys: capabilities.codesys }
    : codesysAgent?.capabilitySnapshot ?? codesysAgent?.capabilities;
  const capabilityView = readCodesysCapabilityView(capabilityRoot);

  const refresh = async () => {
    setRefreshing(true);
    setMessage('');
    try {
      await refreshAgentStatus(projectId);
      if (projectId) setCapabilities(await loadCodesysCapabilities(projectId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : (zh ? '刷新 CODESYS Agent 状态失败。' : 'Failed to refresh CODESYS Agent status.'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const stop = startAgentStatus();
    void refresh();
    return stop;
    // `refresh` intentionally follows the current project only when the dialog opens.
  }, [isOpen, projectId, startAgentStatus]);

  if (!isOpen) return null;

  const createPairingCode = async () => {
    setCreatingCode(true);
    setMessage('');
    try {
      const next = await createRealPlcAgentPairingCode(projectId);
      setPairing(next);
      setMessage(zh
        ? '配对码已创建。请在本机 Agent 的 CloudBridge 中输入。'
        : 'Pairing code created. Enter it in the local Agent CloudBridge.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : (zh ? '创建配对码失败。' : 'Failed to create pairing code.'));
    } finally {
      setCreatingCode(false);
    }
  };

  const copyCode = async () => {
    if (!pairing?.code) return;
    await navigator.clipboard?.writeText(pairing.code);
    setMessage(zh ? '配对码已复制。' : 'Pairing code copied.');
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-[#101418]">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300">
              <Cpu size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">CODESYS Connector</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {zh ? '独立 Worker、独立状态与 CODESYS 原生闭环验证。' : 'Independent Worker, status, and native CODESYS validation loop.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            aria-label={zh ? '关闭' : 'Close'}
          >
            <XCircle size={18} />
          </button>
        </div>

        <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {!projectId ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              {zh
                ? '可以查看本机连接状态，但闭环验证需要项目上下文。请进入项目对话后运行。'
                : 'Local connection status is available, but closed-loop validation requires a project conversation.'}
            </div>
          ) : null}

          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                   {zh ? 'CODESYS Connector 状态' : 'CODESYS Connector status'}
                </h4>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  {zh
                     ? '与 TIA Connector 独立显示；仅配对、任务通道和审批由 RealPLC Agent Core 共享。'
                     : 'Independent from the TIA Connector; only pairing, job transport, and approval are shared by RealPLC Agent Core.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void refresh()}
                disabled={refreshing}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                {zh ? '刷新' : 'Refresh'}
              </button>
            </div>
            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{zh ? '连接' : 'Connection'}</div>
                <div className={`mt-1 flex items-center gap-2 font-semibold ${connectionState === 'online' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {connectionState === 'online'
                    ? <CheckCircle2 size={15} />
                    : statusLoadState === 'detecting' ? <Loader2 size={15} className="animate-spin" /> : <Unplug size={15} />}
                  {connectionState === 'online' ? (zh ? '在线' : 'Online') : (zh ? '离线' : 'Offline')}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">Worker</div>
                <div className={`mt-1 font-semibold ${capabilityView?.ready ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {capabilityView?.ready ? (zh ? '已就绪' : 'Ready') : (zh ? '未就绪' : 'Not ready')}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">IDE</div>
                <div className="mt-1 truncate font-semibold text-zinc-900 dark:text-zinc-100">
                  {capabilityView?.profileName || 'CODESYS V3.5 SP15-SP22'}
                </div>
              </div>
            </div>
            {codesysAgent?.agentName ? (
              <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                {zh ? 'Worker 与 IDE 状态来自 ' : 'Worker and IDE reported by '}
                <span className="font-medium text-zinc-700 dark:text-zinc-300">
                  {codesysAgent.agentName}
                </span>
              </div>
            ) : null}
            {statusLoadState === 'error' && statusError ? (
              <div className="mt-3 text-xs text-red-600 dark:text-red-400">{statusError}</div>
            ) : null}
          </section>

          {capabilityView ? (
            <CodesysCapabilityCard capabilities={capabilityRoot} />
          ) : (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              <div className="font-semibold">{zh ? '尚未收到 CODESYS 能力快照' : 'No CODESYS capability snapshot yet'}</div>
              <div className="mt-1 text-xs leading-relaxed">
                {zh
                  ? '请启动包含独立 CODESYS Worker 的本机 Agent，并确认所选 SP15-SP22 IDE 的 Scripting 组件已安装。'
                  : 'Start the local Agent containing the independent CODESYS Worker and verify Scripting for the selected SP15-SP22 IDE.'}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                   {zh ? '配对 RealPLC Agent Core' : 'Pair RealPLC Agent Core'}
                </h4>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  {zh ? '创建短时配对码；不需要额外授权或云端 CODESYS 费用。' : 'Create a short-lived code; no extra CODESYS cloud or license cost is introduced.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void createPairingCode()}
                disabled={creatingCode}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2 text-sm font-medium text-cyan-700 transition-colors hover:bg-cyan-50 disabled:opacity-60 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200"
              >
                {creatingCode ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
                {zh ? '创建配对码' : 'Create pairing code'}
              </button>
            </div>
            {pairing ? (
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-dashed border-cyan-300 bg-white p-3 dark:border-cyan-500/30 dark:bg-[#0d1117] sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">{zh ? '配对码' : 'Pairing code'}</div>
                  <div className="mt-1 font-mono text-2xl font-semibold tracking-[0.2em] text-zinc-900 dark:text-zinc-100">{pairing.code}</div>
                  <div className="mt-1 text-xs text-zinc-500">{zh ? '失效时间' : 'Expires'}: {formatTime(pairing.expiresAt)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => void copyCode()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200"
                >
                  <Copy size={15} />
                  {zh ? '复制' : 'Copy'}
                </button>
              </div>
            ) : null}
          </section>

          {message ? (
            <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-[#0d1117] dark:text-zinc-300">
              {message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

CodesysAgentSettingsDialog.displayName = 'CodesysAgentSettingsDialog';
